<template>
  <div>
    <button @click="openModal" class="btn">
      <span class="d-flex">
        Campaign&nbsp;<i class="fa-regular fa-bookmark"></i>
      </span>
    </button>

    <Modal
      modalColor="primary"
      size="lg"
      :modalTitle="'Campaign Bookmark'"
      :isShowPopup="showModal"
      :buttons="modalButtons"
      :closeOnBackdrop="true"
      :showCloseButton="true"
      :modalCallBack="onModalCallBack"
    >
      <div class="p-3">
        <ValidationObserver ref="campaignBookmark" v-slot="{ handleSubmit }">
          <form id="campaignBookmark" @submit.prevent="handleSubmit(onSubmit)">
            <CRow class="row mb-2">
              <label class="required col-lg-5 col-md-12">
                Campaign Bookmark Name
              </label>
              <div class="col-lg-6 col-md-6 col-sm-12">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <TextInput
                    name="bookmark_name"
                    :value="bookmark_name"
                    @input="handleInput"
                    :error="errors[0]"
                    :disabled="isLoading"
                  />
                </ValidationProvider>
              </div>
            </CRow>

            <div v-if="isLoading" class="text-center mt-3">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading candidate data...</span>
              </div>
            </div>

            <div v-if="candidateEmails.length > 0 && !isLoading" class="mt-3">
              <small class="text-muted">
                Found {{ candidateEmails.length }} candidate emails
              </small>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </Modal>
  </div>
</template>

<script>
import Vue from "vue";
import { extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import Modal from "../../components/reusable/Modal.vue";
import TextInput from "../../components/reusable/Fields/TextInput.vue";
import { mapActions, mapGetters } from "vuex";
import axios from "axios";

extend("required", { ...required, message: "This field is required" });

export default {
  name: "CampaignBookmark",
  components: {
    Modal,
    TextInput,
  },
  data() {
    return {
      showModal: false,
      bookmark_name: "",
      candidateEmails: [],
      isLoading: false,
      modalButtons: [
        { button: "Cancel", class: "secondary" },
        { button: "Submit", class: "primary" },
      ],
    };
  },
  methods: {
    ...mapActions(["addCampaignBookmark", "showToast"]),

    async openModal() {
      this.resetModalData();
      this.showModal = true;
      await this.fetchCandidateEmails();
    },

    closeModal() {
      this.showModal = false;
      this.resetModalData();
    },

    resetModalData() {
      this.bookmark_name = "";
      this.candidateEmails = [];
      this.isLoading = false;

      if (this.$refs.campaignBookmark) {
        this.$refs.campaignBookmark.reset();
      }
    },

    handleInput(name, value) {
      if (name === "bookmark_name") {
        this.bookmark_name = value;
      }
    },

    async fetchCandidateEmails() {
      try {
        this.isLoading = true;

        const previousUrl = this.previousCandidateDetailAPIData;

        if (!previousUrl) {
          console.warn("No previous candidate detail API URL found");
          this.candidateEmails = [];
          return;
        }

        const response = await axios.get(`${previousUrl}&all_rows=true&specific_column=email`);

        const emails = this.extractEmailsFromResponse(response.data);
        this.candidateEmails = emails;
      } catch (error) {
        this.candidateEmails = [];
      } finally {
        this.isLoading = false;
      }
    },

    extractEmailsFromResponse(data) {
      try {
        if (Array.isArray(data)) {
          return data
            .map((candidate) => candidate.email)
            .filter((email) => email && email.trim());
        }
        return [];
      } catch (error) {
        console.error("Error extracting emails:", error);
        return [];
      }
    },

    async onSubmit() {
      try {
        this.isLoading = true;

        const payload = {
          campaign_name: this.bookmark_name.trim(),
          emails: this.candidateEmails,
        };
        await this.addCampaignBookmark(payload);
        this.closeModal();
      } catch (error) {
        console.error("Error creating bookmark:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async onModalCallBack(action) {
      if (action === false) {
        this.closeModal();
        return;
      }

      const label = (action && action.button) || action;

      switch (label) {
        case "Cancel":
          this.closeModal();
          break;

        case "Submit":
          await this.validateAndSubmit();
          break;

        default:
          this.closeModal();
      }
    },

    async validateAndSubmit() {
      if (this.candidateEmails.length === 0) {
        this.showToast({
          class: "bg-danger text-white",
          message: "No candidate emails found",
        });
        return;
      }
      await this.onSubmit();
    },
  },

  computed: {
    ...mapGetters(["previousCandidateDetailAPIData"]),
  },

  watch: {
    isLoading(newVal) {
      this.modalButtons = [
        { button: "Cancel", class: "secondary", disabled: !!newVal },
        {
          button: newVal ? "Processing..." : "Submit",
          class: "primary",
          disabled: !!newVal,
        },
      ];
    },
  },
};
</script>
